import { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { usersApi, User, Role, CreateUserDto, UpdateUserDto } from '../../api/usersApi';

interface UserFormModalProps {
  open: boolean;
  user: User | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const UserFormModal = ({ open, user, onSuccess, onCancel }: UserFormModalProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const isEdit = !!user;

  useEffect(() => {
    if (open) {
      loadRoles();
      if (user) {
        form.setFieldsValue({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          roleId: user.roleId,
          status: user.status,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, user]);

  const loadRoles = async () => {
    try {
      const data = await usersApi.getRoles();
      setRoles(data);
    } catch (error) {
      message.error('Eroare la încărcarea rolurilor');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEdit && user) {
        const updateData: UpdateUserDto = {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: values.roleId,
          status: values.status,
        };
        await usersApi.update(user.id, updateData);
        message.success('Utilizator actualizat cu succes');
      } else {
        const createData: CreateUserDto = {
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          roleId: values.roleId,
        };
        await usersApi.create(createData);
        message.success('Utilizator creat cu succes');
      }

      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? 'Editează Utilizator' : 'Utilizator Nou'}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      okText={isEdit ? 'Actualizează' : 'Creează'}
      cancelText="Anulează"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { required: true, message: 'Email-ul este obligatoriu' },
            { type: 'email', message: 'Email invalid' },
          ]}
        >
          <Input placeholder="exemplu@hepaderm.ro" />
        </Form.Item>

        {!isEdit && (
          <Form.Item
            label="Parolă"
            name="password"
            rules={[
              { required: true, message: 'Parola este obligatorie' },
              { min: 8, message: 'Parola trebuie să aibă minim 8 caractere' },
            ]}
            extra="Minim 8 caractere, inclusiv majuscule, minuscule, cifre și caractere speciale"
          >
            <Input.Password placeholder="Parolă temporară" />
          </Form.Item>
        )}

        <Form.Item
          label="Nume"
          name="lastName"
          rules={[{ required: true, message: 'Numele este obligatoriu' }]}
        >
          <Input placeholder="Popescu" />
        </Form.Item>

        <Form.Item
          label="Prenume"
          name="firstName"
          rules={[{ required: true, message: 'Prenumele este obligatoriu' }]}
        >
          <Input placeholder="Ion" />
        </Form.Item>

        <Form.Item
          label="Rol"
          name="roleId"
          rules={[{ required: true, message: 'Rolul este obligatoriu' }]}
        >
          <Select
            placeholder="Selectați rolul"
            options={roles.map(role => ({
              label: role.displayName,
              value: role.id,
            }))}
          />
        </Form.Item>

        {isEdit && (
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Statusul este obligatoriu' }]}
          >
            <Select
              options={[
                { label: 'Activ', value: 'ACTIVE' },
                { label: 'Inactiv', value: 'INACTIVE' },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default UserFormModal;
